/**
 * Particles — the signature ambient layer: drifting motes of arcane light.
 *
 * A fixed, pointer-transparent decorative backdrop of slowly floating gold and
 * amethyst motes over the night sky. Purely cosmetic; it carries the brand feel
 * without competing with content. Disabled automatically under reduced-motion via
 * the global stylesheet's animation overrides.
 */
import { useMemo } from "react";

import styles from "./Particles.module.css";

export interface ParticlesProps {
  /** Number of motes to render. Defaults to ``18``. */
  readonly count?: number;
  /** Decorative seed for deterministic placement (e.g. per-page). Defaults to ``1``. */
  readonly seed?: number;
  readonly className?: string;
}

interface Mote {
  readonly left: number;
  readonly top: number;
  readonly size: number;
  readonly delay: number;
  readonly duration: number;
  readonly amethyst: boolean;
}

/** Tiny deterministic PRNG (mulberry32) so SSR/CSR placement matches. */
function makeRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function Particles({
  count = 18,
  seed = 1,
  className,
}: ParticlesProps): JSX.Element {
  const motes = useMemo<ReadonlyArray<Mote>>(() => {
    const rng = makeRandom(seed);
    return Array.from({ length: count }, () => ({
      left: rng() * 100,
      top: rng() * 100,
      size: 1.5 + rng() * 2.5,
      delay: rng() * 6,
      duration: 5 + rng() * 7,
      amethyst: rng() > 0.7,
    }));
  }, [count, seed]);

  return (
    <div
      className={[styles.field, className].filter(Boolean).join(" ")}
      data-ms-particles=""
      aria-hidden="true"
    >
      {motes.map((mote, index) => (
        <span
          key={index}
          className={mote.amethyst ? styles.amethyst : styles.gold}
          style={{
            left: `${mote.left}%`,
            top: `${mote.top}%`,
            width: `${mote.size}px`,
            height: `${mote.size}px`,
            animationDelay: `${mote.delay}s`,
            animationDuration: `${mote.duration}s`,
          }}
        />
      ))}
    </div>
  );
}
